import { useId } from 'react';

type BrandMarkProps = {
  className?: string;
  title?: string;
};

/**
 * The Signal Bloom is a compact, text-free site mark: a luminous bloom held in
 * a circuit horizon. It retains the source site's transformation-and-connection
 * language without reusing its former bracket/asterisk or gender-symbol motifs.
 */
export function BrandMark({ className = '', title = 'bottomsurgery.info' }: BrandMarkProps) {
  const uid = useId().replace(/:/g, '');
  const petal = `signal-petal-${uid}`;
  const trace = `signal-trace-${uid}`;
  const glow = `signal-glow-${uid}`;

  return (
    <svg className={className} viewBox="0 0 176 84" role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg">
      <title>{title}</title>
      <defs>
        <linearGradient id={petal} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#51EEFF" />
          <stop offset=".5" stopColor="#8E83FF" />
          <stop offset="1" stopColor="#FF58C9" />
        </linearGradient>
        <linearGradient id={trace} x1="0" y1="0" x2="1" y2="0">
          <stop stopColor="#4CEAFF" stopOpacity=".22" />
          <stop offset=".5" stopColor="#B287FF" />
          <stop offset="1" stopColor="#FF62CB" stopOpacity=".25" />
        </linearGradient>
        <filter id={glow} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <g fill="none" stroke={`url(#${trace})`} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter">
        <path d="M8 64h26l16-15M8 73h34l14-14M168 64h-26l-16-15M168 73h-34l-14-14" />
        <path d="M44 38V21m10 11V12m68 26V21m-10 11V12" />
      </g>
      <g fill="#060610" stroke={`url(#${trace})`} strokeWidth="1.25">
        <rect x="5.5" y="61.5" width="5" height="5" /><rect x="5.5" y="70.5" width="5" height="5" />
        <rect x="165.5" y="61.5" width="5" height="5" /><rect x="165.5" y="70.5" width="5" height="5" />
        <rect x="41.5" y="35.5" width="5" height="5" /><rect x="51.5" y="29.5" width="5" height="5" />
        <rect x="119.5" y="35.5" width="5" height="5" /><rect x="109.5" y="29.5" width="5" height="5" />
      </g>

      <g fill="#070713" stroke={`url(#${petal})`} strokeWidth="2.7" strokeLinejoin="round" filter={`url(#${glow})`}>
        <path d="M88 60C72 49 65 33 69 18c12 5 20 19 19 42Z" />
        <path d="M88 60c16-11 23-27 19-42-12 5-20 19-19 42Z" />
        <path d="M88 60c-9-15-8-33 0-47 8 14 9 32 0 47Z" />
        <path d="M88 60C69 62 54 56 44 43c17-4 33 2 44 17Z" />
        <path d="M88 60c19 2 34-4 44-17-17-4-33 2-44 17Z" />
      </g>
      <path d="M55 68h66" fill="none" stroke={`url(#${petal})`} strokeWidth="2.25" strokeLinecap="square" />
      <path d="M80 68h16l5 6H75l5-6Z" fill="#070713" stroke={`url(#${petal})`} strokeWidth="1.5" />
      <circle cx="88" cy="60" r="3.2" fill="#F7FFFF" />
    </svg>
  );
}
